import getTextColor from "@/utils/getTextColor";
import {
  startBrandingOverlapDetection,
  applyHostProtection,
} from "@/utils/sanitizeBrandingCss";
import React, { useEffect, useRef } from "react";

interface ChatConfig {
  omniBranding?: boolean;
  theme;
  greetingConfig?: {
    headerFooterBgColor?: string;
  };
}

interface EnvironmentData {
  isAI?: boolean;
}

interface BrandingLogoProps {
  color?: string;
}

interface EntryBrandingProps {
  chatConfig?: ChatConfig;
  environmentData?: EnvironmentData;
  BrandingLogoCBB?: React.ComponentType<BrandingLogoProps>;
  BrandingLogoOmni?: React.ComponentType;
  fromOutgrow?: any;
}

const EntryBranding: React.FC<EntryBrandingProps> = ({
  chatConfig,
  environmentData,
  BrandingLogoCBB,
  BrandingLogoOmni,
  fromOutgrow,
}): JSX.Element | null => {
  const shadowHostRef = useRef<HTMLDivElement>(null);
  const shadowRootRef = useRef<ShadowRoot | null>(null);
  const cleanupRef = useRef<{
    observer?: MutationObserver;
    stopDetection?: () => void;
  }>({});

  useEffect(() => {
    if (!shadowHostRef.current || !chatConfig?.omniBranding) return;

    if (!shadowRootRef.current) {
      shadowRootRef.current = shadowHostRef.current.attachShadow({
        mode: "closed",
      });

      const hostElement = shadowHostRef.current;
      applyHostProtection(hostElement);

      const observer = new MutationObserver(() =>
        applyHostProtection(hostElement),
      );
      observer.observe(hostElement, {
        attributes: true,
        attributeFilter: ["style", "class"],
      });

      const stopDetection = startBrandingOverlapDetection(hostElement);
      cleanupRef.current = { observer, stopDetection };
    }

    const shadowRoot = shadowRootRef.current;
    const backgroundColor = chatConfig?.greetingConfig?.headerFooterBgColor;
    const textColor = getTextColor
      ? getTextColor(chatConfig?.greetingConfig?.headerFooterBgColor)
      : chatConfig?.theme === "dark"
        ? "#ffffff"
        : "#444444";

    const styles = `
      <style>
        :host {
          all: initial !important;
          display: block !important;
          visibility: visible !important;
          opacity: 1 !important;
          position: relative !important;
          z-index: 999999999999 !important;
          font-family: inherit !important;
          font-family: inherit !important;
          width: 100% !important;
          height: auto !important;
          min-width: auto !important;
          min-height: auto !important;
          max-width: none !important;
          max-height: none !important;
          margin: 0 !important;
          padding: 0 !important;
          border: none !important;
          background: transparent !important;
          overflow: visible !important;
           direction: ltr !important ,
        }
        
        .logo-signature {
          display: flex !important;
          align-items: center !important;
          gap: 2px !important;
          justify-content: center !important;
          padding: 7px 13px !important;
          font-size: 12px !important;
          transition: all 0.2s ease !important;
          box-sizing: border-box !important;
          margin-bottom: -3px !important;
          outline: none !important;
          visibility: visible !important;
          opacity: 1 !important;
          height: 100% !important;
          direction: ltr !important;
          background: ${backgroundColor} !important;
          color: ${textColor} !important;
          font-weight: 600 !important;
        }
        
        .logo-signature span {
          font-weight: 600 !important;
          margin: 0 !important;
          padding: 0 !important;
          display: inline !important;
          visibility: visible !important;
          font-family: inherit !important;
          color: ${textColor} !important;
         }
        
        .logo-signature  span a {
          color: ${textColor} !important;
          text-decoration: none !important;
          font-weight: 600 !important;
          font-family: inherit !important;
          transition: opacity 0.2s ease !important;
          display: inline !important;
          font-family: inherit !important;
          visibility: visible !important;
        }
        
        .logo-container {
          display: inline-flex !important;
          align-items: center !important;
          cursor: pointer !important;
          transition: transform 0.2s ease !important;
          visibility: visible !important;
        }
        
        * {
          box-sizing: border-box !important;
          visibility: visible !important;
        }
      </style>
    `;

    const brandingHTML = `
      ${styles}
      <div class="logo-signature">
        <span>
          Powered By
        </span>
        <div class="logo-container flex" data-url="${environmentData?.isAI ? (fromOutgrow ? "https://outgrow.co" : "https://chatbotbuilder.net") : "https://omniengage.co"}">
          ${
            environmentData?.isAI
              ? `${fromOutgrow ? `<img src="https://dlvkyia8i4zmz.cloudfront.net/65cWdRURGyuAMNnIltNw_og_logo.png" alt="Outgrow Logo" style="width:12px; height:12px;" />` : `<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M11.2374 7.32417L11.2362 7.34163V7.35914C11.2362 7.89245 11.1281 8.22884 10.9809 8.44996C10.8369 8.6663 10.6214 8.82361 10.3049 8.93664C9.97956 9.05282 9.56787 9.11458 9.06559 9.14387C8.62435 9.1696 8.14337 9.16916 7.61923 9.16868C7.54762 9.16862 7.47522 9.16855 7.40199 9.16855C7.32877 9.16855 7.25636 9.16862 7.18476 9.16868C6.66062 9.16916 6.17964 9.1696 5.7384 9.14387C5.23612 9.11458 4.82442 9.05282 4.49909 8.93664C4.18261 8.82361 3.96708 8.6663 3.82306 8.44996C3.67585 8.22884 3.56776 7.89245 3.56776 7.35914C3.56776 5.2349 5.291 3.48182 7.40199 3.48182C9.55907 3.48182 11.3861 5.13159 11.2374 7.32417Z" stroke="${textColor}" stroke-width="1.03395"></path><path d="M2.75549 3.99891C-0.0153649 8.08562 2.67627 11.0943 4.85986 10.9699C4.91074 10.967 4.95452 11.0067 4.95452 11.0576V12.9632C4.95452 13.0406 5.04861 13.0787 5.10251 13.0232L7.04553 11.0213C7.08165 10.9841 7.13302 10.9652 7.18471 10.9695C9.88113 11.1931 12.0041 10.6999 12.7894 9.15648C13.7737 7.22207 12.8324 4.84835 11.8847 3.81439C9.9029 1.05719 4.95449 0.755614 2.75549 3.99891Z" stroke="url(#paint0_linear_2541_12675)" stroke-width="1.20628"></path><ellipse cx="5.81734" cy="6.6689" rx="0.540974" ry="0.540973" fill="#FB5F66"></ellipse><ellipse cx="7.44039" cy="6.6689" rx="0.540974" ry="0.540973" fill="#FB5F66"></ellipse><ellipse cx="9.06343" cy="6.6689" rx="0.540974" ry="0.540973" fill="#FB5F66"></ellipse><defs><linearGradient id="paint0_linear_2541_12675" x1="3.35457" y1="1.91882" x2="7.26257" y2="13.1757" gradientUnits="userSpaceOnUse"><stop stop-color="#FF898E"></stop><stop offset="0.546875" stop-color="#EA0D17"></stop><stop offset="1" stop-color="#FB5F66"></stop></linearGradient></defs></svg>`}`
              : `<svg width="20" height="20" viewBox="0 0 20 20" fill="${textColor}">
                 <circle cx="10" cy="10" r="8" />
               </svg>`
          }
        </div>
        <span>
          <a href="${environmentData?.isAI ? (fromOutgrow ? "https://outgrow.co" : "https://chatbotbuilder.net") : "https://omniengage.co"}" target="_blank">
            ${environmentData?.isAI ? (fromOutgrow ? "Outgrow" : "ChatBot Builder") : "Omniengage"}
          </a>
        </span>
      </div>
    `;

    shadowRoot.innerHTML = brandingHTML;

    const logoContainer = shadowRoot.querySelector(".logo-container");
    if (logoContainer) {
      logoContainer.addEventListener("click", () => {
        const url = logoContainer.getAttribute("data-url");
        if (url) {
          window.open(url, "_blank");
        }
      });
    }
    // No cleanup return here!
  }, [
    chatConfig,
    environmentData,
    BrandingLogoCBB,
    BrandingLogoOmni,
    fromOutgrow,
    getTextColor,
  ]);
  useEffect(() => {
    return () => {
      cleanupRef.current.observer?.disconnect();
      cleanupRef.current.stopDetection?.();
      if (shadowRootRef.current) {
        shadowRootRef.current.innerHTML = "";
      }
    };
  }, []);
  useEffect(() => {
    const hostElement = shadowHostRef.current;
    if (!hostElement || !chatConfig?.omniBranding) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (
            entry.intersectionRatio < 0.9 &&
            entry.boundingClientRect.height > 0
          ) {
            try {
              window.parent.postMessage({ type: "BRANDING_CLIPPED" }, "*");
            } catch (e) {}
          }
        });
      },
      { threshold: [0, 0.5, 0.9, 1.0] },
    );

    observer.observe(hostElement);

    return () => {
      observer.disconnect();
    };
  }, [chatConfig]);

  if (!chatConfig?.omniBranding) return null;

  return <div ref={shadowHostRef} data-branding-protected="true"></div>;
};

export default EntryBranding;
